import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { Info } from "lucide-react";

export const OperationalEfficiencyKPIs = ({ data }: any) => {
  const cardConfig = [
    {
      title: "Energy by Facility Type",
      subtitle: "Top 5 facility types consuming high energy",
      tooltip: (
        <ul>
          <li>Queries all facilities in the system</li>
          <li>
            Groups facilities by <strong>Facility Type</strong> attribute (e.g.,
            "Treatment Plants", "Pumping Stations", "Wastewater", "Storage",
            "Admin/Support")
          </li>
          <li>
            For each facility type:
            <ul>
              <li>
                Sums consumption of all facilities of that type using BR-018
                logic
              </li>
              <li>
                Calculates percentage:{" "}
                <code>Type % = (Type Consumption / Total Energy) × 100</code>
              </li>
            </ul>
          </li>
          <li>Sorts all facility types by consumption in descending order</li>
          <li>Selects top 5 facility types</li>
        </ul>
      ),
      data: data?.categoryBreakdown?.facilityType ?? [],
    },
    {
      title: "Energy by System Type",
      subtitle: "Top 5 system types consuming high energy",
      tooltip: (
        <ul>
          <li>Queries all networks in the system</li>
          <li>
            Groups networks by <strong>Network Type</strong> attribute (e.g.,
            "Transmission", "Distribution", "DMA", "Storm Water", "Wastewater")
          </li>
          <li>
            For each network type:
            <ul>
              <li>
                Sums consumption of all networks of that type using BR-020 logic
              </li>
              <li>
                Calculates percentage:{" "}
                <code>Type % = (Type Consumption / Total Energy) × 100</code>
              </li>
            </ul>
          </li>
          <li>Sorts all network types by consumption in descending order</li>
          <li>Selects top 5 network types</li>
        </ul>
      ),
      data: data?.categoryBreakdown?.systemType ?? [],
    },
    {
      title: "Energy by Network Type",
      subtitle: "Top 5 network types consuming high energy",
      tooltip: (
        <ul>
          <li>Queries all networks in the system</li>
          <li>
            Groups networks by <strong>Network Type</strong> attribute (e.g.,
            "Transmission", "Distribution", "DMA", "Storm Water", "Wastewater")
          </li>
          <li>
            For each network type:
            <ul>
              <li>
                Sums consumption of all networks of that type using BR-020 logic
              </li>
              <li>
                Calculates percentage:{" "}
                <code>Type % = (Type Consumption / Total Energy) × 100</code>
              </li>
            </ul>
          </li>
          <li>Sorts all network types by consumption in descending order</li>
          <li>Selects top 5 network types</li>
        </ul>
      ),
      data: data?.categoryBreakdown?.networkType ?? [],
    },
  ];

  const BarChart = ({ data }: { data: any[] }) => {
    return (
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700 font-medium">
                {item.type ?? "NA"}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-semibold">
                  {item.consumptionStr}
                </span>
                <span className="text-gray-500 text-xs min-w-[3rem] text-right">
                  {item.percentageStr}
                </span>
              </div>
            </div>
            <Progress value={item.percentage}></Progress>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Operational Efficiency KPIs</h2>

      {cardConfig.map((card, index) => (
        <Card key={index} className="pt-3">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                {card.title}
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{card.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </div>
            <span className="text-sm text-muted-foreground">
              {card.subtitle}
            </span>
            <p className="text-2xl font-bold mt-2">
              {data?.categoryBreakdown?.totalEnergy} MWh
            </p>
          </CardHeader>
          <CardContent>
            <BarChart data={card.data} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
